export interface ContentAnalysis {
  readabilityScore: number;
  argumentStrength: number;
  structureScore: number;
  themeCoherence: number;
  topicRelevance: number;
  keyThemes: string[];
  keyPhrases: string[];
  suggestedSections: string[];
  missingElements: string[];
  readingLevel: string;
  sentenceComplexity: "simple" | "moderate" | "complex";
  argumentDensity: number;
}

export function analyzeContent(content: string, topic: string): ContentAnalysis {
  const words = content.toLowerCase().split(/\s+/).filter(w => w.length > 0);
  const sentences = content.split(/[.!?]+/).filter(s => s.trim().length > 0);
  const paragraphs = content.split(/\n\n+/).filter(p => p.trim().length > 0);

  // 1. READABILITY SCORE (0-100)
  const readabilityScore = calculateReadability(words, sentences);

  // 2. ARGUMENT STRENGTH (0-100)
  const argumentStrength = calculateArgumentStrength(content, words);

  // 3. STRUCTURE SCORE (0-100)
  const structureScore = calculateStructure(paragraphs, sentences);

  // 4. THEME COHERENCE (0-100)
  const { themeCoherence, keyThemes } = analyzeThemes(content, words);

  // 5. TOPIC RELEVANCE (0-100)
  const topicRelevance = calculateTopicRelevance(content, topic, words);

  // 6. KEY PHRASES
  const keyPhrases = extractKeyPhrases(content, words);

  // 7. MISSING ELEMENTS
  const missingElements = identifyMissingElements(content, topic);

  // 8. SUGGESTED SECTIONS
  const suggestedSections = suggestMissingSections(content, topic, keyThemes);

  // 9. READING LEVEL
  const readingLevel = getReadingLevel(words, sentences);

  // 10. SENTENCE COMPLEXITY
  const sentenceComplexity = analyzeSentenceComplexity(sentences);

  // 11. ARGUMENT DENSITY
  const argumentDensity = (content.match(/therefore|thus|consequently|however|moreover|furthermore|in conclusion/gi) || []).length / Math.max(sentences.length, 1) * 100;

  return {
    readabilityScore,
    argumentStrength,
    structureScore,
    themeCoherence,
    topicRelevance,
    keyThemes,
    keyPhrases,
    suggestedSections,
    missingElements,
    readingLevel,
    sentenceComplexity,
    argumentDensity: Math.min(100, argumentDensity * 10),
  };
}

function calculateReadability(words: string[], sentences: string[]): number {
  if (words.length === 0 || sentences.length === 0) return 0;

  const avgWordsPerSentence = words.length / sentences.length;
  const avgSyllables = words.reduce((sum, word) => sum + countSyllables(word), 0) / words.length;
  
  // Flesch Kincaid Grade Level
  const gradeLevel = 0.39 * avgWordsPerSentence + 11.8 * avgSyllables - 15.59;
  
  // Convert to readability score (100 = perfect readability)
  const score = Math.max(0, 100 - (gradeLevel * 5));
  return Math.min(100, Math.round(score));
}

function calculateArgumentStrength(content: string, words: string[]): number {
  let score = 0;

  // Evidence indicators
  const evidenceKeywords = ['evidence', 'study', 'research', 'data', 'found', 'shows', 'demonstrates', 'proves', 'supports', 'indicates'];
  const evidenceCount = (content.match(new RegExp(evidenceKeywords.join('|'), 'gi')) || []).length;
  score += Math.min(30, evidenceCount * 3);

  // Counterargument handling
  const counterKeywords = ['however', 'although', 'while', 'despite', 'nonetheless', 'conversely', 'on the other hand'];
  const counterCount = (content.match(new RegExp(counterKeywords.join('|'), 'gi')) || []).length;
  score += Math.min(25, counterCount * 5);

  // Logical connectors
  const logicalKeywords = ['therefore', 'thus', 'consequently', 'moreover', 'furthermore', 'in addition', 'additionally'];
  const logicalCount = (content.match(new RegExp(logicalKeywords.join('|'), 'gi')) || []).length;
  score += Math.min(25, logicalCount * 3);

  // Quotes and citations (rough estimation)
  const quoteCount = (content.match(/["'"]/g) || []).length / 2;
  score += Math.min(20, quoteCount * 2);

  return Math.min(100, score);
}

function calculateStructure(paragraphs: string[], sentences: string[]): number {
  let score = 50; // Base score

  // Paragraph count (optimal: 5-15 paragraphs)
  if (paragraphs.length >= 5 && paragraphs.length <= 15) {
    score += 20;
  } else if (paragraphs.length >= 3) {
    score += 10;
  }

  // Paragraph length consistency
  const avgParaLength = sentences.length / Math.max(paragraphs.length, 1);
  if (avgParaLength >= 3 && avgParaLength <= 8) {
    score += 15;
  } else if (avgParaLength >= 2) {
    score += 8;
  }

  // Variety in paragraph length
  const paraSentenceCounts = paragraphs.map(p => p.split(/[.!?]+/).length);
  const variance = calculateVariance(paraSentenceCounts);
  if (variance > 2) {
    score += 15;
  }

  return Math.min(100, score);
}

function analyzeThemes(content: string, words: string[]): { themeCoherence: number; keyThemes: string[] } {
  const themeCounts: { [key: string]: number } = {};
  
  // Extract common noun patterns (simplified)
  const nounPatterns = content.match(/\b[A-Z][a-z]+(?:\s+[a-z]+)*\b/g) || [];
  
  nounPatterns.forEach(pattern => {
    themeCounts[pattern] = (themeCounts[pattern] || 0) + 1;
  });

  // Get top themes
  const keyThemes = Object.entries(themeCounts)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 5)
    .map(([theme]) => theme);

  // Theme coherence: how well themes repeat
  const totalThemeOccurrences = Object.values(themeCounts).reduce((a, b) => a + b, 0);
  const themeCoherence = Math.min(100, (totalThemeOccurrences / Math.max(words.length / 10, 1)) * 10);

  return { themeCoherence: Math.round(themeCoherence), keyThemes };
}

function calculateTopicRelevance(content: string, topic: string, words: string[]): number {
  if (!topic || words.length === 0) return 0;

  const topicWords = topic.toLowerCase().split(/\s+/).filter(w => w.length > 2);
  const contentLower = content.toLowerCase();

  let relevanceScore = 0;

  // Exact phrase matches (highest weight)
  const phraseMatches = (contentLower.match(new RegExp(topic.toLowerCase(), 'g')) || []).length;
  relevanceScore += phraseMatches * 10;

  // Individual topic word matches
  const wordMatches = topicWords.filter(tw => words.includes(tw)).length;
  relevanceScore += (wordMatches / Math.max(topicWords.length, 1)) * 50;

  // Proximity of topic words
  for (let i = 0; i < words.length - topicWords.length; i++) {
    const window = words.slice(i, i + 5).join(' ');
    if (topicWords.every(tw => window.includes(tw))) {
      relevanceScore += 5;
    }
  }

  return Math.min(100, Math.round(relevanceScore));
}

function extractKeyPhrases(content: string, words: string[]): string[] {
  const phrases: { [key: string]: number } = {};

  // Extract 2-3 word phrases
  for (let i = 0; i < words.length - 1; i++) {
    if (words[i].length > 3 && words[i + 1].length > 3) {
      const phrase = `${words[i]} ${words[i + 1]}`;
      if (!isCommonWord(words[i]) && !isCommonWord(words[i + 1])) {
        phrases[phrase] = (phrases[phrase] || 0) + 1;
      }
    }
  }

  return Object.entries(phrases)
    .sort(([, a], [, b]) => b - a)
    .slice(0, 8)
    .map(([phrase]) => phrase);
}

function identifyMissingElements(content: string, topic: string): string[] {
  const missing: string[] = [];

  // Check for introduction
  if (content.length > 0 && content.slice(0, 200).split(/\s+/).length < 20) {
    missing.push("Strong introduction");
  }

  // Check for conclusion
  if (!content.match(/in conclusion|to conclude|finally|in summary/i)) {
    missing.push("Clear conclusion");
  }

  // Check for citations/references
  if (!content.match(/\([0-9]+\)|et al\.|Smith et al|Johnson et al/i)) {
    missing.push("Citations and references");
  }

  // Check for transitions
  if ((content.match(/however|furthermore|moreover|additionally/gi) || []).length < 3) {
    missing.push("Transition words");
  }

  // Check for specific examples
  if ((content.match(/for example|such as|instance|case study/gi) || []).length === 0) {
    missing.push("Concrete examples");
  }

  return missing;
}

function suggestMissingSections(content: string, topic: string, themes: string[]): string[] {
  const sections: string[] = [];

  if (!content.match(/introduction|background/i)) {
    sections.push("Introduction & Background");
  }

  if (themes.length > 0 && !content.match(/analysis|discussion/i)) {
    sections.push("Analysis & Discussion");
  }

  if (!content.match(/conclusion|summary|implications/i)) {
    sections.push("Conclusion & Implications");
  }

  if (content.match(/research|study|experiment/i) && !content.match(/methodology|methods/i)) {
    sections.push("Methodology");
  }

  if (!content.match(/limitations|critique|weaknesses/i)) {
    sections.push("Limitations & Critiques");
  }

  return sections;
}

function getReadingLevel(words: string[], sentences: string[]): string {
  if (words.length === 0 || sentences.length === 0) return "Unknown";

  const avgWordsPerSentence = words.length / sentences.length;
  const avgSyllables = words.reduce((sum, word) => sum + countSyllables(word), 0) / words.length;
  
  const gradeLevel = 0.39 * avgWordsPerSentence + 11.8 * avgSyllables - 15.59;

  if (gradeLevel < 6) return "High School";
  if (gradeLevel < 9) return "Undergraduate";
  if (gradeLevel < 12) return "Graduate";
  return "Advanced Academic";
}

function analyzeSentenceComplexity(sentences: string[]): "simple" | "moderate" | "complex" {
  const avgWords = sentences.reduce((sum, s) => sum + s.split(/\s+/).length, 0) / Math.max(sentences.length, 1);
  const clauseCount = sentences.reduce((sum, s) => sum + (s.match(/,|;|:/g) || []).length, 0) / Math.max(sentences.length, 1);

  if (avgWords < 12 && clauseCount < 0.5) return "simple";
  if (avgWords < 18 && clauseCount < 1.5) return "moderate";
  return "complex";
}

function countSyllables(word: string): number {
  word = word.toLowerCase();
  if (word.length <= 3) return 1;

  let count = 0;
  let previousWasVowel = false;

  for (let i = 0; i < word.length; i++) {
    const isVowel = 'aeiouy'.includes(word[i]);
    if (isVowel && !previousWasVowel) count++;
    previousWasVowel = isVowel;
  }

  if (word.endsWith('e')) count--;
  if (word.endsWith('le')) count++;

  return Math.max(1, count);
}

function calculateVariance(values: number[]): number {
  if (values.length === 0) return 0;
  const mean = values.reduce((a, b) => a + b, 0) / values.length;
  const variance = values.reduce((sum, val) => sum + Math.pow(val - mean, 2), 0) / values.length;
  return Math.sqrt(variance);
}

function isCommonWord(word: string): boolean {
  const common = ['the', 'and', 'or', 'but', 'of', 'in', 'to', 'a', 'is', 'that', 'it', 'for', 'this', 'on', 'as', 'by', 'with', 'be', 'have', 'from'];
  return common.includes(word);
}

export function generateActionableSuggestions(analysis: ContentAnalysis, topic: string): string[] {
  const suggestions: string[] = [];

  // Readability suggestions
  if (analysis.readabilityScore < 60) {
    suggestions.push("✏️ Break up complex sentences for better clarity");
    suggestions.push("📖 Use simpler vocabulary to improve readability");
  }

  // Argument strength suggestions
  if (analysis.argumentStrength < 50) {
    suggestions.push("📚 Add more research evidence and citations");
    suggestions.push("🔄 Include counterarguments to strengthen your position");
    suggestions.push("💡 Use logical connectors (therefore, moreover, etc.)");
  } else if (analysis.argumentStrength < 75) {
    suggestions.push("✅ Good arguments! Add 1-2 more supporting examples");
  }

  // Structure suggestions
  if (analysis.structureScore < 60) {
    suggestions.push("📋 Organize into clear paragraphs (5-8 sentences each)");
    suggestions.push("🎯 Group related ideas together");
  }

  // Topic relevance suggestions
  if (analysis.topicRelevance < 50) {
    suggestions.push(`🎯 Strengthen connection to: "${topic}"`);
    suggestions.push(`📍 Mention your topic "${topic}" more frequently`);
  } else if (analysis.topicRelevance < 80) {
    suggestions.push("🔗 Add more specific examples related to your topic");
  }

  // Missing elements
  analysis.missingElements.forEach(element => {
    suggestions.push(`➕ Add: ${element}`);
  });

  // Theme suggestions
  if (analysis.keyThemes.length > 0) {
    suggestions.push(`💬 Key themes: ${analysis.keyThemes.slice(0, 3).join(", ")}`);
  }

  // Complexity suggestions
  if (analysis.sentenceComplexity === "simple") {
    suggestions.push("🎓 Add more complex sentence structures for depth");
  }

  return suggestions.slice(0, 8); // Return top 8 suggestions
}
